import { useMemo, useRef } from "react";

const TOOLTIP_CURSOR_OFFSET = 14;
const VIEWPORT_EDGE_PADDING = 8;
const DEFAULT_CUTOUT = "70%";

export const clampTooltipToViewport = (cursorX, cursorY, width, height) => {
  let x = cursorX + TOOLTIP_CURSOR_OFFSET;
  let y = cursorY - height / 2;
  if (x + width > window.innerWidth - VIEWPORT_EDGE_PADDING) {
    x = cursorX - width - TOOLTIP_CURSOR_OFFSET;
  }
  if (y < VIEWPORT_EDGE_PADDING) {
    y = VIEWPORT_EDGE_PADDING;
  } else if (y + height > window.innerHeight - VIEWPORT_EDGE_PADDING) {
    y = window.innerHeight - height - VIEWPORT_EDGE_PADDING;
  }
  return { x, y };
};

const hideTooltipElement = (el) => {
  el.style.opacity = "0";
  el.style.visibility = "hidden";
};

export const createExternalTooltipRenderer =
  ({ formatBody }) =>
  ({ chart, tooltip }, refs) => {
    const el = refs.tooltip.current;
    if (!el) {
      return;
    }
    if (tooltip.opacity === 0) {
      hideTooltipElement(el);
      return;
    }
    const item = tooltip.dataPoints?.[0];
    if (!item) {
      return;
    }
    if (refs.title.current) {
      refs.title.current.textContent = item.label ?? "";
    }
    if (refs.body.current) {
      refs.body.current.textContent = formatBody(item);
    }
    el.style.visibility = "visible";
    el.style.opacity = "1";
    const canvasRect = chart.canvas.getBoundingClientRect();
    const { x, y } = clampTooltipToViewport(
      canvasRect.left + tooltip.caretX,
      canvasRect.top + tooltip.caretY,
      el.offsetWidth,
      el.offsetHeight
    );
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  };

export const useDonutChartOptions = ({
  formatBody,
  tooltipPosition,
  cutout = DEFAULT_CUTOUT,
}) => {
  const tooltipRef = useRef(null);
  const titleRef = useRef(null);
  const bodyRef = useRef(null);

  const renderTooltip = useMemo(
    () => createExternalTooltipRenderer({ formatBody }),
    [formatBody]
  );

  const chartOptions = useMemo(
    () => ({
      cutout,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          ...(tooltipPosition ? { position: tooltipPosition } : {}),
          external: (context) =>
            renderTooltip(context, {
              tooltip: tooltipRef,
              title: titleRef,
              body: bodyRef,
            }),
        },
      },
    }),
    [renderTooltip, tooltipPosition, cutout]
  );

  return { chartOptions, tooltipRef, titleRef, bodyRef };
};
